import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Briefcase, Save, Phone, ChevronRight, Share2, Gift, Copy } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import VerificationBanner from '../../components/VerificationBanner';
import { getCurrentUser, getProfile } from '../../services/auth';
import api from '../../services/api';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    // Account Information
    email: '',
    username: '',
    
    // Personal Information
    firstName: '',
    lastName: '',
    personalPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Business Information
    companyName: '',
    jobTitle: '', 
    businessPhone: '',
    businessSize: 'small',
    employeeCount: '',
    industry: '',
  });
  
  // const [referralCode] = useState('TORQUE-' + Math.random().toString(36).substring(2, 10).toUpperCase());
  // const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();
        
        if (!user) {
          navigate('/login');
          return;
        }
        
        // Try to get the latest profile data from the API
        try {
          const profile = await getProfile();
          setFormData({
            // Account Information
            email: profile.email || '',
            username: profile.username || '',
            
            // Personal Information
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            personalPhone: profile.personalPhone || '',
            address: profile.address || '',
            city: profile.city || '',
            state: profile.state || '',
            zipCode: profile.zipCode || '',
            
            // Business Information
            companyName: profile.companyName || '',
            jobTitle: profile.jobTitle || '',
            businessPhone: profile.businessPhone || '',
            businessSize: profile.businessSize || 'small',
            employeeCount: profile.employeeCount || '',
            industry: profile.industry || '',
          });
        } catch (error) {
          // Fallback to stored user data if API fails
          setFormData({
            // Account Information
            email: user.email || '',
            username: user.username || '',
            
            // Personal Information
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            personalPhone: user.personalPhone || '',
            address: user.address || '',
            city: user.city || '',
            state: user.state || '',
            zipCode: user.zipCode || '',
            
            // Business Information
            companyName: user.companyName || '',
            jobTitle: user.jobTitle || '',
            businessPhone: user.businessPhone || '',
            businessSize: user.businessSize || 'small',
            employeeCount: user.employeeCount || '',
            industry: user.industry || '',
          });
        }
      } catch (err) {
        setError('Failed to load profile data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear success message when form is changed
    if (success) {
      setSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      
      // Make API call to update profile
      await api.put('/User/profile', formData);
      
      // Update local storage with new user data
      const user = getCurrentUser();
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };


  // const copyReferralCode = () => {
  //   navigator.clipboard.writeText(referralCode);
  //   setShowCopiedMessage(true);
  //   setTimeout(() => {
  //     setShowCopiedMessage(false);
  //   }, 2000);
  // };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 color-transition">
      <Navbar />
      <VerificationBanner />

      <div className="flex-grow container mx-auto px-4 sm:px-5 lg:px-7 py-10 mt-16">
        {/* Account navigation breadcrumbs */}
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">Home</Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to="/account/profile" className="text-blue-600 dark:text-blue-400 font-medium">Profile</Link>
        </div>

        {/* Account navigation tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-8">
          <Link 
            to="/account/profile" 
            className="px-4 py-2 text-sm font-medium border-b-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400"
            aria-current="page"
          >
            Profile
          </Link>
          <Link 
            to="/account/subscription" 
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Subscription
          </Link>
          <Link 
            to="/account/settings" 
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Settings
          </Link>
          {/* <Link
            to="/account/referrals"
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Referrals
          </Link> */}
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-blue-600 to-blue-800 h-32 p-6">
              <h1 className="text-2xl font-bold text-white">Your Profile</h1>
              <p className="text-blue-100 mt-1">Manage your personal information</p>
            </div>
            
            <div className="p-6">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-md mb-6 flex items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm">{error}</p>
                      </div>
                    </div>
                  )}
                  
                  {success && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 px-4 py-3 rounded-md mb-6 flex items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm">Profile updated successfully!</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-10">
                    {/* Account Information Section */}
                    <div>
                      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Account Information</h2>
                      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Email
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Mail className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="email"
                              name="email"
                              id="email"
                              value={formData.email}
                              onChange={handleChange}
                              className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                              placeholder="john.doe@example.com"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Username
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <User className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              name="username"
                              id="username"
                              value={formData.username}
                              onChange={handleChange}
                              className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                              placeholder="johndoe"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Personal Information Section */}
                    <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Personal Information</h2>
                      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div>
                          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            First Name
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <User className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              name="firstName"
                              id="firstName"
                              value={formData.firstName}
                              onChange={handleChange}
                              className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                              placeholder="John"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Last Name
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <User className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              name="lastName"
                              id="lastName"
                              value={formData.lastName}
                              onChange={handleChange}
                              className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                              placeholder="Doe"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="personalPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Personal Phone
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Phone className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="tel"
                              name="personalPhone"
                              id="personalPhone"
                              value={formData.personalPhone}
                              onChange={handleChange}
                              className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                              placeholder="(555) 123-4567"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Address
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <input
                              type="text"
                              name="address"
                              id="address"
                              value={formData.address}
                              onChange={handleChange}
                              className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                              placeholder="123 Main St"
                            />
                          </div>
                        </div>
                        
                        <div className="md:col-span-2 grid grid-cols-3 gap-4">
                          <div>
                            <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              City
                            </label>
                            <div className="relative rounded-md">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              <input
                                type="text"
                                name="city"
                                id="city"
                                value={formData.city}
                                onChange={handleChange}
                                className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                                placeholder="San Francisco"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              State
                            </label>
                            <div className="relative rounded-md">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                                </svg>
                              </div>
                              <input
                                type="text"
                                name="state"
                                id="state"
                                value={formData.state}
                                onChange={handleChange}
                                className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                                placeholder="CA"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              ZIP Code
                            </label>
                            <div className="relative rounded-md">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              </div>
                              <input
                                type="text"
                                name="zipCode"
                                id="zipCode"
                                value={formData.zipCode}
                                onChange={handleChange}
                                className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                                placeholder="94103"
                              />
                            </div>
                          </div>
                      </div>
                    </div>
                  </div>
                    
                    {/* Work Information Section */}
                    <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Work Information</h2>
                      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div>
                          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Company Name
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Briefcase className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              name="companyName"
                              id="companyName"
                              value={formData.companyName}
                              onChange={handleChange}
                              className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                              placeholder="Your Auto Repair Shop"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Job Title
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Briefcase className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              name="jobTitle"
                              id="jobTitle"
                              value={formData.jobTitle}
                              onChange={handleChange}
                              className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                              placeholder="Shop Owner"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="businessPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Business Phone Number
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Phone className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="tel"
                              name="businessPhone"
                              id="businessPhone"
                              value={formData.businessPhone}
                              onChange={handleChange}
                              className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                              placeholder="(555) 123-4567"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="businessSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Business Size
                            </label>
                            <div className="relative rounded-md">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                                </svg>
                              </div>
                              <select
                                id="businessSize"
                                name="businessSize"
                                value={formData.businessSize}
                                onChange={handleChange}
                                className="pl-10 pr-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11 appearance-none"
                              >
                                <option value="sole_proprietor">Sole Proprietor</option>
                                <option value="small">Small Business (1-10 employees)</option>
                                <option value="medium">Medium Business (11-50 employees)</option>
                                <option value="large">Large Business (51+ employees)</option>
                              </select>
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="employeeCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Number of Employees
                            </label>
                            <div className="relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              </div>
                              <input
                                type="number"
                                id="employeeCount"
                                name="employeeCount"
                                value={formData.employeeCount}
                                onChange={handleChange}
                                className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                                placeholder="5"
                                min="1"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Industry
                          </label>
                          <div className="relative rounded-md">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                            </div>
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                              </svg>
                            </div>
                            <select
                              id="industry"
                              name="industry"
                              value={formData.industry}
                              onChange={handleChange}
                              className="pl-10 pr-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11 appearance-none"
                            >
                              <option value="">Select Industry</option>
                              <option value="automotive">Automotive</option>
                              <option value="repair">Repair & Maintenance</option>
                              <option value="service">Service Center</option>
                              <option value="dealership">Dealership</option>
                              <option value="parts">Auto Parts</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Referral Section - Commented out - keeping code for future use */}
                  {/* <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700" id="referrals">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                      <div className="flex items-center">
                        <Share2 className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                        Referral Program
                      </div>
                    </h2>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-6 rounded-lg border border-blue-100 dark:border-blue-800 mb-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div className="mb-4 md:mb-0">
                          <h3 className="text-blue-800 dark:text-blue-300 font-medium text-lg mb-2">Invite friends &amp; earn free time</h3>
                          <p className="text-gray-600 dark:text-gray-300 text-sm">
                            Share your referral code with friends and colleagues. When they sign up, they get a free month and you get closer to earning 2 years of premium access!
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <Gift className="h-12 w-12 text-blue-500 dark:text-blue-400 mx-auto md:mx-0" />
                        </div>
                      </div>

                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Your Referral Code
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={referralCode}
                            readOnly
                            className="block w-full px-4 py-3 bg-white dark:bg-gray-800 text-lg font-mono text-center border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={copyReferralCode}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                            title="Copy to clipboard"
                          >
                            <Copy className="h-5 w-5" />
                          </button>

                          {showCopiedMessage && (
                            <div className="absolute right-10 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded shadow-lg">
                              Copied!
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 flex flex-wrap gap-3">
                        <a
                          href={`mailto:?subject=Try%20Torque%20Software&body=I%20think%20you'll%20love%20Torque.%20Use%20my%20referral%20code%20${referralCode}%20to%20get%20a%20free%20month%20of%20premium%20access!%20https://torque-software.com/register`}
                          className="flex-1 min-w-[120px] inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Email
                        </a>
                        <button
                          type="button"
                          className="flex-1 min-w-[120px] inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Twitter
                        </button>
                        <button
                          type="button"
                          className="flex-1 min-w-[120px] inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          LinkedIn
                        </button>
                      </div>
                    </div>
                  </div> */}
                  
                  <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    <button
                      type="button"
                      className="mr-3 inline-flex items-center px-5 py-2.5 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-70"
                    >
                      {saving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ProfilePage;